import { useMemo, useState } from "react"
import { useOutletContext } from "react-router-dom"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Grid"
import Stack from "@mui/material/Stack"
import { Box, CircularProgress, useMediaQuery, useTheme } from "@mui/material"
import { DateTime } from "luxon"
import { useTranslation } from "react-i18next"

import type { DeviceDashboardOutletContext } from "types/dashboard.types"
import type { LegacyTowerDevicePositionPlotValues } from "types/devicePosition.types"

import {
  getTowerPositionVariant,
  getTowerPositionConfig,
} from "helpers/utils/devicePositionTower"
import { formatPlotTitleTooltip } from "helpers/utils/plots"
import { degreesToString } from "helpers/utils/common"
import { MD_ROW_HEIGHT, SM_ROW_HEIGHT } from "helpers/utils/constants"
import { useDashboardContext } from "helpers/hooks/useDashboardContext"
import useTower from "helpers/hooks/towers/useTower"
import PlotHelpTooltip from "widgets/plots/PlotHelpTooltip"
import PlotDialogIcon from "widgets/plots/PlotDialogIcon"
import { PlotContainer, PositionBox } from "widgets/styled/containers"
import { TowerViewDialog } from "widgets/plots/dialogs/TowerViewDialog"
import SkeletonBox from "widgets/common/SkeletonBox"
import { TowerAngleView } from "./TowerAngleView"
import PositionGrid from "./PositionGrid"

interface LegacyTowerDevicePositionPlotProps {
  values: LegacyTowerDevicePositionPlotValues
  isLoading?: boolean
}

const LegacyTowerDevicePositionPlot = ({
  values,
  isLoading = false,
}: LegacyTowerDevicePositionPlotProps) => {
  const theme = useTheme()
  const { device } = useOutletContext<DeviceDashboardOutletContext>()
  const isSmall = useMediaQuery(theme.breakpoints.only("sm"))
  const { rowHeight: contextRowHeight } = useDashboardContext()
  const rowHeight = contextRowHeight ?? (isSmall ? SM_ROW_HEIGHT : MD_ROW_HEIGHT)
  const { alpha, beta, _time } = values
  const { t } = useTranslation()
  const [infoPopupOpen, setInfoPopupOpen] = useState(false)

  const { tower, isLoading: isTowerLoading } = useTower({
    id: device.settings?.tower_id,
  })

  const variant = useMemo(
    () => getTowerPositionVariant(device, tower?.type),
    [device, tower?.type],
  )

  const { viewSrcs, viewStyle, tooltipKey } = getTowerPositionConfig(variant)

  const tooltipContent = useMemo(
    () => formatPlotTitleTooltip([tooltipKey], t),
    [tooltipKey, t],
  )

  return (
    <>
      <PlotContainer
        type=""
        disableGutters
        maxWidth="xl"
        sx={{
          height: { xs: "auto", sm: `${rowHeight * 5}px` },
          marginTop: "1rem",
          marginLeft: 0,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          justifyContent="space-between"
          mb="0.5rem"
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h5">
                  {t("plot_titles.LAST_POSITION") + ":"}
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: "nowrap", fontWeight: 400 }}
              >
                {DateTime.fromMillis(_time).toFormat("yyyy-MM-dd HH:mm:ss")}
              </Typography>
              {(isLoading || isTowerLoading) && <CircularProgress size={20} />}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
            {tooltipContent && <PlotHelpTooltip content={tooltipContent} />}
            {!isTowerLoading && <PlotDialogIcon onClick={() => setInfoPopupOpen(true)} />}
          </Stack>
        </Stack>

        {!isTowerLoading ? (
          <PositionBox
            sx={{
              overflow: { xs: "visible", sm: "hidden" },
              height: { xs: "auto", sm: "95%" },
              pb: { xs: "2rem", sm: 0 },
            }}
          >
            <PositionGrid />
            <Grid
              container
              height={{ xs: "auto", sm: "100%" }}
              spacing={{ xs: 5, sm: 1 }}
            >
              <Grid item xs={12} sm={6}>
                <TowerAngleView
                  src={viewSrcs.first}
                  viewStyle={viewStyle}
                  rowHeight={rowHeight}
                  value={alpha}
                  viewKey="first"
                  displayName={`α = ${degreesToString(alpha)}`}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TowerAngleView
                  src={viewSrcs.second}
                  viewStyle={viewStyle}
                  rowHeight={rowHeight}
                  value={beta}
                  viewKey="second"
                  displayName={`β = ${degreesToString(beta)}`}
                />
              </Grid>
            </Grid>
          </PositionBox>
        ) : (
          <SkeletonBox height={rowHeight * 4} />
        )}
      </PlotContainer>

      <TowerViewDialog
        open={infoPopupOpen}
        onClose={() => setInfoPopupOpen(false)}
        variant={variant}
      />
    </>
  )
}

export default LegacyTowerDevicePositionPlot
